import {
  Component,
  Host,
  h,
  Prop,
  State,
  Element,
  getAssetPath
} from "@stencil/core";

import { MercuryBundles } from "@genexus/mercury";
import { Locale } from "../../common/locale";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "utils/elevation",
  "components/button",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-about",
  styleUrl: "about.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/about"]
})
export class GxIdeAbout {
  #componentLocale: any;
  #nextLogo = getAssetPath(`./gx-ide-assets/about/images/next-logo.svg`);

  @Element() el: HTMLGxIdeAboutElement;

  /**
   * The application version to display in the dialog.
   */
  @Prop() readonly appVersion: string;

  /**
   * GeneXus Next version, and also related dependencies versions
   */
  @Prop() readonly versions: string[] = [];

  /**
   * Callback invoked when the user clicks on "Details & Acknowledgements"
   */
  @Prop() readonly detailsCallback: DetailsCallback;

  /**
   * Callback invoked when the user clicks on "Privacy policy"
   */
  @Prop() readonly privacyPolicyCallback: PrivacyPolicyCallback;

  /**
   * Callback invoked when the user clicks on "Terms and conditions"
   */
  @Prop() readonly termsAndConditionsCallback: TermsAndConditionsCallback;

  /**
   * True when the user copies the versions
   */
  @State() copying: boolean = false;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  #copyVersionsHandler = () => {
    this.copying = true;
    setTimeout(() => {
      this.copying = false;
    }, 1000);

    // be cautions with spaces and indentaiton on the following .writeText
    navigator.clipboard.writeText(
      `${this.appVersion}\n
${this.#componentLocale.copyVersionsAdditionalText}${this.versions
        .map(version => `- ${version}`)
        .join("\n")}`
    );
  };

  render() {
    const renderPrivacyAndTerms =
      this.privacyPolicyCallback && this.termsAndConditionsCallback;
    const renderPrivacyOrTerms =
      this.privacyPolicyCallback || this.termsAndConditionsCallback;

    return (
      <Host class="scrollable">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          <header class="header">
            <figure class="logo__container">
              <img
                class="logo__img"
                src={this.#nextLogo}
                alt={this.#componentLocale.logoAlt}
              />
              <figcaption class="body-semi-bold-m logotype">
                GeneXus Next
              </figcaption>
            </figure>
            {this.appVersion && (
              <p class="body-reuglar-m">
                {this.#componentLocale.nextVersion}{" "}
                <span>{this.appVersion}</span>
              </p>
            )}
          </header>
          <div class="main">
            {this.versions.length ? (
              <div class="versions__container elevation-1">
                <ul class="versions__list scrollable">
                  {this.versions.map(version => {
                    return <li class="body-regular-s">{version}</li>;
                  })}
                </ul>
                <div class="versions__container-footer">
                  <button
                    class="button-secondary"
                    type="button"
                    onClick={this.#copyVersionsHandler}
                    disabled={this.copying}
                  >
                    {this.copying
                      ? this.#componentLocale.copied
                      : this.#componentLocale.copy}
                    {}
                  </button>
                </div>
              </div>
            ) : null}

            {this.detailsCallback && (
              <button
                class="button-tertiary"
                type="button"
                onClick={this.detailsCallback}
              >
                {this.#componentLocale.detailsAndAcknowledgmentes}
              </button>
            )}
          </div>

          <footer class="body-regular-s footer">
            {renderPrivacyOrTerms && (
              <div class="links-container" hidden={!renderPrivacyAndTerms}>
                {this.privacyPolicyCallback && (
                  <button
                    class="link-underlined caption-regular-m"
                    type="button"
                    onClick={this.privacyPolicyCallback}
                  >
                    {this.#componentLocale.privacyPolicy}
                  </button>
                )}

                {renderPrivacyAndTerms && "|"}

                {this.termsAndConditionsCallback && (
                  <button
                    class="link-underlined caption-regular-m"
                    type="button"
                    onClick={this.termsAndConditionsCallback}
                  >
                    {this.#componentLocale.termsAndConditions}
                  </button>
                )}
              </div>
            )}
            {this.#componentLocale.copyright}
          </footer>
        </section>
      </Host>
    );
  }
}

export type DetailsCallback = () => Promise<void>;
export type PrivacyPolicyCallback = () => Promise<void>;
export type TermsAndConditionsCallback = () => Promise<void>;
